import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  SxProps,
  Typography,
  useTheme,
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import React, { FC } from 'react';

import { CinemaModel } from '@/logic/services/cinemas';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterSkeleton from './movie-filters-skeleton';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';

import { pxToRem } from '@/theme/typography';
import { useCity } from '@/logic/hooks';
import ImageBoxFuture from '@/components/shared/image-box-future';

const LocationIconBlack = '/icons/movie-details/location-icon-black.svg';

interface CinemasFilterProps {
  typographySx: SxProps;
  cinemas: CinemaModel[];
  selectedCinemasFilter: number[];
  isFetchingCinemas: boolean;
  returnSelectedCinemas: (cinemasIds: number[]) => void;
  showDisabledFilterMessage: () => void;
}

export const CinemasFilter: FC<CinemasFilterProps> = ({
  typographySx,
  cinemas,
  selectedCinemasFilter,
  isFetchingCinemas,
  returnSelectedCinemas,
  showDisabledFilterMessage,
}) => {
  const theme = useTheme();
  const intl = useIntl();

  const { cityId, getCity } = useCity();
  const selectedCity = getCity(cityId)?.name;
  const handleCinemaChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (checkIfCinemaDisabled(Number(event.target.value))) {
      showDisabledFilterMessage();
      return;
    }
    if (selectedCinemasFilter.indexOf(Number(event.target.value)) < 0)
      returnSelectedCinemas([...selectedCinemasFilter, Number(event.target.value)]);
    else {
      const index = selectedCinemasFilter.indexOf(Number(event.target.value));
      const _selectedCinemasFilter = [];
      Object.assign(_selectedCinemasFilter, selectedCinemasFilter);
      _selectedCinemasFilter.splice(index, 1);
      returnSelectedCinemas([..._selectedCinemasFilter]);
    }
  };

  const checkIfCinemaDisabled = (id: number): boolean => {
    return cinemas.find(cinema => cinema.id === id)?.isDisabled;
  };
  const checkIfCinemaChecked = (id: number): boolean => {
    return selectedCinemasFilter.find(cinemaId => cinemaId === id) ? true : false;
  };

  return (
    <Accordion defaultExpanded>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.grey[90] }} />}
        id='movie-details-cinemas-filter'
        sx={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: 'transparent',
        }}
      >
        <Box
          component='div'
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginInlineEnd: '18px',
          }}
        >
          <ImageBoxFuture
            img={LocationIconBlack}
            title={intl.formatMessage({
              id: 'images.title.location',
            })}
            objectFit='contain'
            sx={{
              width: '20px',
              height: '20px',
            }}
          />
        </Box>
        <Typography sx={typographySx}>
          <FormattedMessage id='cinema' />
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ margin: pxToRem(24), marginTop: 0 }}>
        <Typography sx={{ ...typographySx, textAlign: 'start' }}>
          {selectedCity}
        </Typography>
        <FormGroup>
          {!isFetchingCinemas &&
            cinemas?.map(cinema => (
              <FormControlLabel
                key={cinema.id}
                control={
                  <Checkbox
                    value={cinema.id}
                    defaultChecked={checkIfCinemaChecked(cinema.id)}
                    indeterminateIcon={<IndeterminateCheckBoxIcon />}
                    indeterminate={checkIfCinemaDisabled(cinema.id)}
                    sx={{
                      '&.Mui-disabled': {
                        color: theme.palette.grey[30],
                      },
                      '&.MuiCheckbox-indeterminate': {
                        color: theme.palette.grey[30],
                      },
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                    }}
                    onChange={handleCinemaChange}
                  />
                }
                label={cinema.name}
              />
            ))}

          {isFetchingCinemas &&
            [...Array(cinemas?.length || 5)].map((i, _i: number) => (
              <FilterSkeleton key={_i} />
            ))}
        </FormGroup>
      </AccordionDetails>
    </Accordion>
  );
};

export default CinemasFilter;
